<template>
  <div
    class="product-card bg-white rounded-xl border border-gray-100 h-full flex flex-col relative group scroll-container shadow-sm hover:shadow-md transition-all duration-200">
    <!-- Ana Ürün Linki -->
    <NuxtLink :to="getUrlWithVendorId(selectedColor?.url || colors[0]?.url)" class="block flex-1">
      <!-- Resim Alanı -->
      <div class="relative aspect-[4/5] overflow-hidden bg-gray-50 rounded-t-xl">
        <NuxtImg :src="selectedImage ? 'cl/' + selectedImage : '/img-placeholder.jpg'" :alt="product.name" format="webp"
          width="360" height="480" quality="80" loading="lazy" fit="inside" background="white"
          placeholder="/placeholder-card.webp" />

        <!-- Aksiyon Butonları -->
        <UButton
          class="absolute right-2 top-2 w-8 h-8 bg-white/90 text-slate-500  backdrop-blur-sm shadow-sm rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-all duration-200"
          :class="product.is_favorite ? 'text-red-500' : 'text-slate-500'"
          :icon="product.is_favorite ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'"
          @click.prevent="$emit('toggle-favorite')" />

        <!-- İndirim Etiketi -->
        <div v-if="selectedDiscount"
          class="absolute top-2 left-2 bg-orange-500 text-white text-xs font-medium px-2 py-1 rounded transform-gpu">
          %{{ selectedDiscount }}
        </div>
      </div>

      <!-- İçerik Alanı -->
      <div class="p-2 sm:p-3 flex flex-col">
        <!-- Marka -->
        <div class="text-xs font-medium text-orange-600 mb-1 line-clamp-1">
          {{ product.brand?.name }}
        </div>

        <!-- Ürün Adı -->
        <h3 class="font-medium text-gray-700 text-sm line-clamp-2 mb-2">
          {{ product.name }}
        </h3>

        <!-- Renk Seçenekleri -->
        <div v-if="colors.length > 1" class="mt-auto flex flex-wrap gap-1 sm:gap-1.5">
          <button v-for="color in colors" :key="color.id"
            class="w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 transform-gpu" :class="[
              selectedColor?.id === color.id
                ? 'border-orange-500 shadow-md scale-110'
                : 'border-gray-200'
            ]" @click.prevent="selectColor(color)">
            <span class="absolute inset-0.5 rounded-full" :class="getColorClass(color.name)"></span>
            <span class="sr-only">{{ color.name }}</span>
          </button>
        </div>

        <!-- Tek Renk Gösterimi -->
        <div v-else-if="colors.length === 1" class="mt-2 flex items-center gap-1.5">
          <div class="w-4 h-4 rounded-full border border-gray-200 relative transform-gpu">
            <span class="absolute inset-0.5 rounded-full" :class="getColorClass(colors[0].name)"></span>
          </div>
          <span class="text-xs text-gray-500">{{ colors[0].name }}</span>
        </div>

        <!-- Stok Durumu -->
        <div v-if="showLowStock" class="mt-2 text-xs text-red-500 flex items-center gap-1">
          <UIcon name="i-heroicons-exclamation-circle" class="w-4 h-4" />
          <span>Son
            <span class="font-medium">{{ selectedColor.stock_qty }}</span>
            ürün</span>
        </div>

        <!-- Yıldız Puanı -->
        <!-- <div class="flex items-center gap-1 mb-2 mt-auto">
          <div class="flex gap-px">
            <UIcon
              v-for="i in 5"
              :key="i"
              name="i-heroicons-star-solid"
              class="w-4 h-4 text-amber-400"
            />
          </div>
          <span class="text-xs text-gray-400">(97)</span>
        </div> -->
      </div>
    </NuxtLink>

    <!-- Fiyat ve Sepet Alanı -->
    <div class="p-2 sm:p-3 border-t border-gray-100">
      <div>
        <div v-if="selectedPrice > 0" class="flex items-center gap-1.5">
          <p class="text-sm sm:text-base font-semibold text-gray-900">
            {{ formatPrice(selectedSalePrice) }}
          </p>
          <p v-if="selectedDiscount" class="text-xs text-gray-400 line-through">
            {{ formatPrice(selectedPrice) }}
          </p>
        </div>
        <p v-else class="text-gray-500 italic text-xs">Fiyat Sorunuz</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRoute } from 'vue-router'

const props = defineProps({
  product: {
    type: Object,
    required: true
  }
})

const route = useRoute()

// Memoized computed properties
const colors = computed(() => props.product.colors || [])
const selectedColor = shallowRef(colors.value[0])

// Seçili renk için memoized computed properties
const selectedPrice = computed(() => selectedColor.value?.price || 0)
const selectedSalePrice = computed(() => selectedColor.value?.sale_price || 0)
const selectedDiscount = computed(
  () => selectedColor.value?.discount_rate || null
)

// Görsel seçimi için optimize edilmiş computed
const selectedImage = computed(() => {
  if (!selectedColor.value) return props.product.product_image || null
  return (
    selectedColor.value.images?.[0]?.path || props.product.product_image || null
  )
})

// Düşük stok kontrolü için optimize edilmiş computed
const showLowStock = computed(() => {
  const stockQty = selectedColor.value?.stock_qty
  return stockQty !== undefined && stockQty <= 5
})

// Renk seçimi için optimize edilmiş fonksiyon
const selectColor = (color) => {
  if (color.id === selectedColor.value?.id) return
  selectedColor.value = color
}

// Renk sınıfları için optimize edilmiş map
const colorClassMap = {
  Siyah: 'bg-black',
  Beyaz: 'bg-white',
  Kırmızı: 'bg-red-500',
  Mavi: 'bg-blue-500',
  Yeşil: 'bg-green-500',
  Sarı: 'bg-yellow-500',
  Mor: 'bg-purple-500',
  Pembe: 'bg-pink-500',
  Gri: 'bg-gray-500',
  Kahverengi: 'bg-amber-800',
  Turuncu: 'bg-orange-500',
  Lacivert: 'bg-blue-900',
  Bej: 'bg-[#F5F5DC]',
  Bordo: 'bg-red-900',
  Altın: 'bg-yellow-600',
  Gümüş: 'bg-gray-300',
  Bronz: 'bg-amber-600',
  'Çok Renkli': 'bg-gradient-to-r from-red-500 via-green-500 to-blue-500',
  'Titanyum Gri': 'bg-gray-400',
  'Uzay Grisi': 'bg-gray-700'
}

const getColorClass = (colorName) => colorClassMap[colorName] || 'bg-gray-300'

defineEmits(['add-to-cart', 'toggle-favorite'])

const getUrlWithVendorId = (url) => {
  if (!url) return url

  // Mağaza sayfasında olup olmadığımızı kontrol et
  const isVendorPage = route.path.includes('/magaza/')
  if (!isVendorPage) return url

  // URL pattern: /magaza/[slug]-m-[id]
  const match = route.path.match(/\/magaza\/.*-m-(\d+)$/)
  if (match && match[1]) {
    const vendorId = match[1]
    // Eğer URL'de mid parametresi yoksa ekle
    if (!url.includes('mid=')) {
      const separator = url.includes('?') ? '&' : '?'
      return `${url}${separator}mid=${vendorId}`
    }
  }

  return url
}
</script>

<style scoped>
.product-card {
  contain: content;
  content-visibility: auto;
  contain-intrinsic-size: 0 500px;
}

@media (prefers-reduced-motion: reduce) {
  .product-card * {
    animation: none !important;
    transition: none !important;
  }
}
</style>
